import {
  BreakingNewsArticleProps,
  BreakingNewsGroup,
} from "@routes/BreakingNews/utils/types";
import { SanitizedFeedSectionArticle } from "@typings/Cue";
import { IS_PLAYWRIGHT, PLAYWRIGHT_DATE } from "@util/constant";
import dayjs from "dayjs";

export const getBreakingNewsProps = (
  articles: SanitizedFeedSectionArticle[]
) => {
  const cardProps = articles.map(
    ({
      id,
      title,
      updated,
      edited,
      sections,
      paywall,
      displaySetting,
      urlPath,
    }) => {
      const props: BreakingNewsArticleProps = {
        id,
        edited,
        updated,
        section: {
          name: sections?.[0]?.name || "",
          uniqueName: sections?.[0]?.uniqueName || "",
        },
        paywall: paywall?.contentAccess === "1",
        hasUpdatedTimestampDisplay: displaySetting?.hasUpdatedTimeDisplayed,
        title: displaySetting?.displayHeadline || title,
        allSections: sections?.map((section) => section.uniqueName) || [],
        slug: urlPath,
      };

      return props;
    }
  );

  return JSON.parse(JSON.stringify(cardProps));
};

/** Helper function to get the day in Singapore time. */
export const daySG = (day?: string) => {
  if (IS_PLAYWRIGHT && typeof day === "undefined") {
    // This is the date we recorded the data for the breaking news page.
    // Update this date when you want to change the date of the breaking news page.
    return dayjs(PLAYWRIGHT_DATE);
  }

  return dayjs(day).utcOffset(8);
};

/** Helper function to compare the updated and edited time of two articles. */
export const compareUpdatedEdited = (
  a: SanitizedFeedSectionArticle,
  b: SanitizedFeedSectionArticle
) => {
  const latestA = daySG(a.updated).isAfter(a.edited) ? a.updated : a.edited;
  const latestB = daySG(b.updated).isAfter(b.edited) ? b.updated : b.edited;

  return daySG(latestB).diff(daySG(latestA));
};

export function getGroupTitle(group: BreakingNewsGroup) {
  switch (group) {
    case "today":
    case "yesterday":
      return group;
    case "thedaybefore":
      if (IS_PLAYWRIGHT) return "OCT 07";

      return dayjs().subtract(2, "day").format("MMM DD");
  }
}

export function getFilteredArticles(
  articles: BreakingNewsArticleProps[],
  filter: string | null
) {
  if (!filter) return articles;

  return articles.filter(({ allSections }) => {
    return allSections.includes(filter.replaceAll("/", "_"));
  });
}

/**
 * Helper function to determine which date is later
 * @param dateA The first date to compare.
 * @param dateB The second date to compare.
 * @returns {string}
 */
export const getLaterTime = (
  dateA: string,
  dateB: string
): string | undefined => {
  if (!dayjs(dateA).isValid()) return dateB;
  if (!dayjs(dateB).isValid()) return dateA;

  if (dayjs(dateA).isAfter(dayjs(dateB))) return dateA;

  return dateB;
};
